(() => {
  'use strict';

  const SELECTORS = {
    root: '[data-rc-carousel]',
    viewport: '[data-rc-carousel-viewport]',
    track: '[data-rc-carousel-track]',
    slide: '[data-rc-carousel-slide]',
    prev: '[data-rc-carousel-prev]',
    next: '[data-rc-carousel-next]',
    dots: '[data-rc-carousel-dots]'
  };

  const instances = new WeakMap();

  const clamp = (value, min, max) => Math.max(min, Math.min(value, max));

  const debounce = (fn, delay = 120) => {
    let timeout;
    return (...args) => {
      window.clearTimeout(timeout);
      timeout = window.setTimeout(() => fn(...args), delay);
    };
  };

  class RCCarousel {
    constructor(root) {
      this.root = root;
      this.viewport = root.querySelector(SELECTORS.viewport);
      this.track = root.querySelector(SELECTORS.track);
      this.slides = Array.from(root.querySelectorAll(SELECTORS.slide));
      this.prevButton = root.querySelector(SELECTORS.prev);
      this.nextButton = root.querySelector(SELECTORS.next);
      this.dotsContainer = root.querySelector(SELECTORS.dots);

      this.index = 0;
      this.maxIndex = 0;
      this.step = 0;
      this.visibleCards = 1;
      this.isDragging = false;
      this.hasDragged = false;
      this.startX = 0;
      this.currentX = 0;
      this.dragDelta = 0;
      this.pointerId = null;
      this.animationFrame = null;

      this.onResize = debounce(() => this.refresh(), 140);

      if (!this.viewport || !this.track || this.slides.length === 0) return;

      this.bindEvents();
      this.setupObservers();
      this.refresh();
    }

    bindEvents() {
      this.prevButton?.addEventListener('click', () => this.goTo(this.index - 1));
      this.nextButton?.addEventListener('click', () => this.goTo(this.index + 1));

      this.viewport.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.goTo(this.index - 1);
        }
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.goTo(this.index + 1);
        }
        if (event.key === 'Home') {
          event.preventDefault();
          this.goTo(0);
        }
        if (event.key === 'End') {
          event.preventDefault();
          this.goTo(this.maxIndex);
        }
      });

      this.viewport.addEventListener('pointerdown', (event) => this.onPointerDown(event));
      this.viewport.addEventListener('pointermove', (event) => this.onPointerMove(event));
      this.viewport.addEventListener('pointerup', (event) => this.onPointerUp(event));
      this.viewport.addEventListener('pointercancel', (event) => this.onPointerUp(event));
      this.viewport.addEventListener('lostpointercapture', (event) => this.onPointerUp(event));

      this.track.addEventListener('click', (event) => {
        if (!this.hasDragged) return;
        const link = event.target.closest('a');
        if (link) {
          event.preventDefault();
          event.stopPropagation();
        }
      }, true);

      window.addEventListener('resize', this.onResize);
    }

    setupObservers() {
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.onResize);
        this.resizeObserver.observe(this.root);
        this.resizeObserver.observe(this.viewport);
      }

      if ('IntersectionObserver' in window) {
        this.intersectionObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.refresh();
            }
          });
        }, { threshold: 0.1 });
        this.intersectionObserver.observe(this.root);
      }
    }

    getVisibleCardsFromCSS() {
      const value = window.getComputedStyle(this.root).getPropertyValue('--rc-current-cards').trim();
      const parsed = Number.parseFloat(value);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
    }

    getGap() {
      const styles = window.getComputedStyle(this.track);
      const gap = Number.parseFloat(styles.columnGap || styles.gap || '0');
      return Number.isFinite(gap) ? gap : 0;
    }

    refresh() {
      this.slides = Array.from(this.root.querySelectorAll(SELECTORS.slide));
      if (!this.slides.length) return;

      this.visibleCards = this.getVisibleCardsFromCSS();
      const firstSlide = this.slides[0];
      const gap = this.getGap();

      this.step = firstSlide.getBoundingClientRect().width + gap;

      const wholeVisibleCards = Math.max(1, Math.floor(this.visibleCards));
      this.maxIndex = Math.max(0, this.slides.length - wholeVisibleCards);
      this.index = clamp(this.index, 0, this.maxIndex);

      this.buildDots();
      this.update();
      this.equalizeHeights();
    }

    equalizeHeights() {
      this.slides.forEach((slide) => {
        slide.style.minHeight = '';
      });

      const maxHeight = Math.max(...this.slides.map((slide) => slide.getBoundingClientRect().height));
      if (Number.isFinite(maxHeight)) {
        this.slides.forEach((slide) => {
          slide.style.minHeight = `${Math.ceil(maxHeight)}px`;
        });
      }
    }

    buildDots() {
      if (!this.dotsContainer) return;

      const dotCount = this.maxIndex + 1;
      this.dotsContainer.innerHTML = '';

      if (dotCount <= 1 || this.root.dataset.showDots === 'false') {
        this.dotsContainer.hidden = true;
        return;
      }

      this.dotsContainer.hidden = false;

      for (let i = 0; i < dotCount; i += 1) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'rc-carousel__dot';
        dot.setAttribute('aria-label', `Zu Slide ${i + 1} wechseln`);
        dot.addEventListener('click', () => this.goTo(i));
        this.dotsContainer.appendChild(dot);
      }
    }

    goTo(index) {
      this.index = clamp(index, 0, this.maxIndex);
      this.update();
    }

    next() {
      this.goTo(this.index + 1);
    }

    prev() {
      this.goTo(this.index - 1);
    }

    update(extraOffset = 0) {
      const baseOffset = -(this.index * this.step);
      this.track.style.transform = `translate3d(${baseOffset + extraOffset}px, 0, 0)`;
      this.updateArrows();
      this.updateDots();
      this.updateAccessibility();
    }

    updateArrows() {
      const shouldHideControls = this.maxIndex <= 0 || this.root.dataset.showArrows === 'false';

      if (this.prevButton) {
        const hidden = shouldHideControls || this.index <= 0;
        this.prevButton.disabled = hidden;
        this.prevButton.classList.toggle('is-hidden', hidden);
        this.prevButton.setAttribute('aria-hidden', hidden ? 'true' : 'false');
      }

      if (this.nextButton) {
        const hidden = shouldHideControls || this.index >= this.maxIndex;
        this.nextButton.disabled = hidden;
        this.nextButton.classList.toggle('is-hidden', hidden);
        this.nextButton.setAttribute('aria-hidden', hidden ? 'true' : 'false');
      }
    }

    updateDots() {
      if (!this.dotsContainer || this.dotsContainer.hidden) return;
      const dots = Array.from(this.dotsContainer.querySelectorAll('.rc-carousel__dot'));
      dots.forEach((dot, dotIndex) => {
        const active = dotIndex === this.index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
      });
    }

    updateAccessibility() {
      const visibleCount = Math.max(1, Math.floor(this.visibleCards));
      this.slides.forEach((slide, slideIndex) => {
        const visible = slideIndex >= this.index && slideIndex < this.index + visibleCount;
        slide.setAttribute('aria-hidden', visible ? 'false' : 'true');
      });
    }

    onPointerDown(event) {
      if (event.button !== undefined && event.button !== 0) return;
      if (this.maxIndex <= 0) return;

      this.isDragging = true;
      this.hasDragged = false;
      this.pointerId = event.pointerId;
      this.startX = event.clientX;
      this.currentX = event.clientX;
      this.dragDelta = 0;

      this.root.classList.add('is-dragging');

      try {
        this.viewport.setPointerCapture(event.pointerId);
      } catch (error) {}
    }

    onPointerMove(event) {
      if (!this.isDragging || event.pointerId !== this.pointerId) return;

      this.currentX = event.clientX;
      this.dragDelta = this.currentX - this.startX;

      if (Math.abs(this.dragDelta) > 4) this.hasDragged = true;

      const resistance =
        (this.index <= 0 && this.dragDelta > 0) ||
        (this.index >= this.maxIndex && this.dragDelta < 0)
          ? 0.25
          : 1;

      const visualDelta = this.dragDelta * resistance;

      if (this.animationFrame) window.cancelAnimationFrame(this.animationFrame);

      this.animationFrame = window.requestAnimationFrame(() => {
        this.update(visualDelta);
      });
    }

    onPointerUp(event) {
      if (!this.isDragging || event.pointerId !== this.pointerId) return;

      this.isDragging = false;
      this.pointerId = null;
      this.root.classList.remove('is-dragging');

      const threshold = Math.max(40, this.step * 0.16);

      if (this.dragDelta <= -threshold) this.next();
      else if (this.dragDelta >= threshold) this.prev();
      else this.update();

      this.dragDelta = 0;

      window.setTimeout(() => {
        this.hasDragged = false;
      }, 80);
    }

    destroy() {
      window.removeEventListener('resize', this.onResize);
      this.resizeObserver?.disconnect();
      this.intersectionObserver?.disconnect();
      if (this.animationFrame) window.cancelAnimationFrame(this.animationFrame);
    }
  }

  const initCarousel = (root) => {
    if (!root || instances.has(root)) return;
    const instance = new RCCarousel(root);
    instances.set(root, instance);
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll(SELECTORS.root).forEach(initCarousel);
  };

  document.addEventListener('DOMContentLoaded', () => initAll());

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll(SELECTORS.root).forEach((root) => {
      const instance = instances.get(root);
      if (instance) {
        instance.destroy();
        instances.delete(root);
      }
    });
  });

  if (document.readyState === 'interactive' || document.readyState === 'complete') {
    initAll();
  }
})();
